import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';
import {
  Subject,
  fromEvent,
  merge,
  timer,
  takeUntil,
  debounceTime,
  switchMap,
  filter,
  from,
} from 'rxjs';
import { SsoState } from '../../../store/sso/sso.state';
import {
  SsoRefreshTokenAction,
  SsoSetUserInfoAction,
  SsoClearStateAction,
} from '../../../store/sso/sso.actions';
import { IDecodedToken } from '../../interfaces/sso.interface';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshManagerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private refreshTimer$ = new Subject<void>();
  private isUserActive = true;
  private lastActivityTime = Date.now();
  private storageListener: ((event: StorageEvent) => void) | null = null;

  // Configuración
  private readonly REFRESH_BUFFER_MINUTES = 2; // Refrescar 2 minutos antes de expirar
  private readonly ACTIVITY_TIMEOUT_MINUTES = 5; // Considerar inactivo después de 5 min
  private readonly CHECK_INTERVAL_SECONDS = 30; // Verificar cada 30 segundos

  constructor(
    private store: Store,
    private ngZone: NgZone
  ) {
    this.initializeActivityTracking();
    this.initializeTokenMonitoring();
    this.initializeCrossTabSync(); // ✅ Inicializar sincronización entre pestañas
  }

  /**
   * Inicializa el tracking de actividad del usuario
   */
  private initializeActivityTracking(): void {
    // Eventos que indican actividad del usuario
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Page Visibility API para detectar cuando la pestaña está activa
    const visibilityChange$ = fromEvent(document, 'visibilitychange');

    // Merge de todos los eventos de actividad
    const userActivity$ = merge(
      ...activityEvents.map(event => fromEvent(document, event)),
      visibilityChange$
    );

    // Debounce para evitar demasiadas actualizaciones
    userActivity$
      .pipe(
        debounceTime(1000), // Agrupa eventos en ventanas de 1 segundo
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateUserActivity();
      });
  }

  /**
   * Inicializa el monitoreo del token
   */
  private initializeTokenMonitoring(): void {
    // Timer que verifica el estado del token cada X segundos
    timer(0, this.CHECK_INTERVAL_SECONDS * 1000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => from(this.checkTokenExpiration())),
        filter(shouldRefresh => shouldRefresh)
      )
      .subscribe(() => {
        this.performProactiveRefresh();
      });
  }

  /**
   * Actualiza el estado de actividad del usuario
   */
  private updateUserActivity(): void {
    this.lastActivityTime = Date.now();
    this.isUserActive = !document.hidden; // Page Visibility API

    // Log solo si cambió la visibilidad (más silencioso)
    // console.log('🟢 [TokenRefreshManager] Usuario activo:', {
    //   timestamp: new Date().toLocaleTimeString(),
    //   isVisible: !document.hidden,
    // });
  }

  /**
   * Verifica si el token necesita ser refrescado
   */
  private checkTokenExpiration(): Promise<boolean> {
    return new Promise(resolve => {
      const token = this.store.selectSnapshot(SsoState.token);

      if (!token) {
        resolve(false);
        return;
      }

      try {
        const decoded = jwtDecode(token) as IDecodedToken;
        const now = Math.floor(Date.now() / 1000); // Tiempo actual en segundos
        const expirationTime = decoded.exp;
        const timeUntilExpiration = expirationTime - now;
        const refreshThreshold = this.REFRESH_BUFFER_MINUTES * 60; // Convertir a segundos

        // Verificar si el usuario ha estado inactivo por mucho tiempo
        const timeSinceLastActivity =
          (Date.now() - this.lastActivityTime) / 1000 / 60; // en minutos
        const isUserRecentlyActive =
          timeSinceLastActivity < this.ACTIVITY_TIMEOUT_MINUTES;

        const shouldRefresh =
          timeUntilExpiration <= refreshThreshold &&
          this.isUserActive &&
          isUserRecentlyActive;

        resolve(shouldRefresh);
      } catch (error) {
        console.error('❌ Error decodificando token:', error);
        resolve(false);
      }
    });
  }

  /**
   * Ejecuta el refresh proactivo del token
   */
  private performProactiveRefresh(): void {
    const refreshToken = this.store.selectSnapshot(SsoState.refreshToken);

    if (!refreshToken) {
      console.warn('⚠️ No hay refresh token disponible para refresh proactivo');
      return;
    }

    console.log(
      '🔄 [TokenRefreshManager] Iniciando refresh proactivo del token...'
    );

    this.store.dispatch(new SsoRefreshTokenAction(refreshToken)).subscribe({
      next: () => {
        const newToken = this.store.selectSnapshot(SsoState.token);
        console.log('✅ [TokenRefreshManager] Token refrescado exitosamente', {
          newTokenPrefix: newToken ? newToken.substring(0, 20) : 'null',
          timestamp: new Date().toLocaleTimeString(),
        });

        // Verificar que se escribió en localStorage después de un pequeño delay
        // NGXS Storage Plugin escribe de forma asíncrona
        setTimeout(() => {
          this.verifyLocalStorageSync();
        }, 500); // Aumentado a 500ms para dar tiempo a NGXS Storage Plugin
      },
      error: error => {
        console.error(
          '❌ [TokenRefreshManager] Error en refresh proactivo:',
          error
        );
      },
    });
  }

  /**
   * Verifica que el token actualizado esté en localStorage
   */
  private verifyLocalStorageSync(): void {
    try {
      const storeToken = this.store.selectSnapshot(SsoState.token);
      const storeRefreshToken = this.store.selectSnapshot(
        SsoState.refreshToken
      );

      console.log('🔍 [TokenRefreshManager] Verificando localStorage:', {
        storeTokenExists: !!storeToken,
        storeRefreshTokenExists: !!storeRefreshToken,
        allKeys: Object.keys(localStorage),
      });

      // NGXS guarda cada state por separado, buscar en 'sso' no en '@@STATE'
      const storedSsoState = localStorage.getItem('sso');
      if (storedSsoState) {
        const ssoState = JSON.parse(storedSsoState);

        console.log('📦 [TokenRefreshManager] Estado SSO en localStorage:', {
          ssoTokenExists: !!ssoState?.token,
          ssoRefreshTokenExists: !!ssoState?.refreshToken,
          ssoKeys: ssoState ? Object.keys(ssoState) : [],
        });

        if (ssoState?.token === storeToken) {
          console.log(
            '✅ [TokenRefreshManager] Token sincronizado correctamente en localStorage'
          );
        } else {
          console.warn(
            '⚠️ [TokenRefreshManager] Token en localStorage NO coincide con store:',
            {
              storeTokenPrefix: storeToken
                ? storeToken.substring(0, 20)
                : 'null',
              localStorageTokenPrefix: ssoState?.token
                ? ssoState.token.substring(0, 20)
                : 'null',
              areEqual: ssoState?.token === storeToken,
            }
          );
        }
      } else {
        console.warn(
          '⚠️ [TokenRefreshManager] No se encontró "sso" en localStorage',
          {
            localStorageLength: localStorage.length,
            allKeys: Object.keys(localStorage),
          }
        );
      }
    } catch (error) {
      console.error(
        '❌ [TokenRefreshManager] Error verificando localStorage:',
        error
      );
    }
  }

  /**
   * Obtiene información del estado actual del token
   */
  getTokenInfo(): {
    timeUntilExpiration: number;
    shouldRefresh: boolean;
  } | null {
    const token = this.store.selectSnapshot(SsoState.token);

    if (!token) return null;

    try {
      const decoded = jwtDecode(token) as IDecodedToken;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = decoded.exp - now;
      const refreshThreshold = this.REFRESH_BUFFER_MINUTES * 60;

      return {
        timeUntilExpiration,
        shouldRefresh: timeUntilExpiration <= refreshThreshold,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Fuerza un refresh inmediato (para testing o casos especiales)
   */
  forceRefresh(): void {
    this.performProactiveRefresh();
  }

  /**
   * ✅ Inicializa la sincronización entre pestañas del navegador
   * Detecta cambios en localStorage desde otras pestañas y sincroniza el token local
   */
  private initializeCrossTabSync(): void {
    this.storageListener = (event: StorageEvent) => {
      // Solo procesar cambios en la key 'sso' de localStorage
      // (NGXS guarda cada state por separado, no bajo @@STATE)
      if (event.key === 'sso' || event.key === null) {
        this.handleStorageChange(event);
      }
    };

    window.addEventListener('storage', this.storageListener);
    console.log(
      '🔄 [TokenRefreshManager] Sincronización entre pestañas activada'
    );
  }

  /**
   * ✅ Maneja los cambios en localStorage desde otras pestañas
   */
  private handleStorageChange(event: StorageEvent): void {
    console.log('🔍 [TokenRefreshManager] Storage event detectado:', {
      key: event.key,
      hasNewValue: !!event.newValue,
      hasOldValue: !!event.oldValue,
    });

    try {
      // Si newValue es null, significa que se limpió el storage (logout en otra pestaña)
      if (event.newValue === null && event.oldValue !== null) {
        console.log(
          '🚪 [TokenRefreshManager] Logout detectado en otra pestaña'
        );
        this.store.dispatch(new SsoClearStateAction());
        return;
      }

      // Si hay un nuevo valor, parsearlo y verificar cambios en SSO
      // event.newValue es el objeto SSO directamente (no un wrapper)
      if (event.newValue) {
        const newSsoState = JSON.parse(event.newValue);
        const oldSsoState = event.oldValue ? JSON.parse(event.oldValue) : null;

        console.log('📊 [TokenRefreshManager] Estados SSO comparados:', {
          newTokenExists: !!newSsoState?.token,
          oldTokenExists: !!oldSsoState?.token,
          newToken: newSsoState?.token
            ? `${newSsoState.token.substring(0, 20)}...`
            : null,
          oldToken: oldSsoState?.token
            ? `${oldSsoState.token.substring(0, 20)}...`
            : null,
        });

        // Verificar si el SSO tiene token
        if (newSsoState && newSsoState.token) {
          const newToken = newSsoState.token;
          const oldToken = oldSsoState?.token;

          // Obtener el token actual del store local para comparación
          const currentLocalToken = this.store.selectSnapshot(SsoState.token);

          // Si el token cambió Y es diferente al token local actual
          if (
            newToken &&
            newToken !== oldToken &&
            newToken !== currentLocalToken
          ) {
            console.log(
              '🔄 [TokenRefreshManager] Token actualizado en otra pestaña, sincronizando...',
              {
                newTokenPrefix: newToken.substring(0, 20),
                oldTokenPrefix: oldToken ? oldToken.substring(0, 20) : 'null',
                currentLocalPrefix: currentLocalToken
                  ? currentLocalToken.substring(0, 20)
                  : 'null',
              }
            );

            // Actualizar el store local con los nuevos datos
            // skipVerification: true para evitar llamar verify-token
            // (el token ya fue validado en la pestaña que hizo el refresh)
            if (newSsoState.token && newSsoState.refreshToken) {
              this.store.dispatch(
                new SsoSetUserInfoAction(
                  {
                    accessToken: newSsoState.token,
                    refreshToken: newSsoState.refreshToken,
                  },
                  true // skipVerification: true
                )
              );
              console.log(
                '✅ [TokenRefreshManager] Estado SSO sincronizado correctamente (sin verificación)'
              );
            } else {
              console.warn(
                '⚠️ [TokenRefreshManager] Token o refreshToken faltantes'
              );
            }
          } else if (newToken && newToken === currentLocalToken) {
            console.log(
              'ℹ️ [TokenRefreshManager] Token ya sincronizado (sin cambios)'
            );
          }

          // Si el token se eliminó en la otra pestaña (logout)
          if (!newToken && oldToken) {
            console.log(
              '🚪 [TokenRefreshManager] Logout detectado (token eliminado)'
            );
            this.store.dispatch(new SsoClearStateAction());
          }
        } else if (oldSsoState?.token && !newSsoState?.token) {
          // Si tenía token pero ya no, es un logout
          console.log(
            '🚪 [TokenRefreshManager] Logout detectado (SSO sin token)'
          );
          this.store.dispatch(new SsoClearStateAction());
        }
      }
    } catch (error) {
      console.error(
        '❌ [TokenRefreshManager] Error al sincronizar entre pestañas:',
        error
      );
    }
  }

  ngOnDestroy(): void {
    // Limpiar listener de storage
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      console.log(
        '🔄 [TokenRefreshManager] Sincronización entre pestañas desactivada'
      );
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.refreshTimer$.complete();
  }
}
